import React, { useEffect, useState } from 'react';
import { Form, FormInstance } from 'antd';
import {
  DatePicker,
  Input,
  Modal,
  Select,
  Spinner,
} from 'UI';
import { UnitMapping } from 'constants/unit';
import { Organization, Resource } from 'constants/interface';
import { useDispatch, useSelector } from 'hooks/store';
import {
  cleanResourcesAction,
  getResourceListAction,
  isLoadingResourcesSelector,
  resourcesEntitiesSelector,
  resourcesIdsSelector,
} from 'state/slices/resource';
import dayjs, { Dayjs } from 'dayjs';
import {
  CostCenterEnum,
  InventoryCostCenterMapping,
  InventoryOutputTypeEnum,
  InventoryOutputReasonMapping,
} from 'constants/inventory';
import { PlusOutlined } from '@ant-design/icons';
import { ResourceCategoryEnum } from 'constants/resource';

import {
  FormContainer,
  BaseFormItem,
  InlineButton,
  RemoteSelectContent,
  RemoteSelectSpinnerContainer,
  QuantityFormItem,
  NotesInline,
  EntryItemFormRow,
  EntryItemInput,
  RemoveEntryItemButton,
  RemoveEntryItemIcon,
  AddEntryItemButton,
  FormItemRow,
} from './style';

interface EntryItemFormData {
  resource: string
  quantity: string
}

export interface EntryOutputFormData {
  entryDate: Dayjs
  costCenter: CostCenterEnum
  outputType: InventoryOutputTypeEnum
  notes: string
  items: EntryItemFormData[]
}

interface EntryOutputModalProps {
  formHandler: FormInstance
  onSubmit(data: unknown): void
  onCancel(): void
  isLoading: boolean
  isVisible: boolean
  activeOrganization: Organization
  resourceCategory: ResourceCategoryEnum
}

const EntryOutputModal = ({
  formHandler,
  onSubmit,
  onCancel,
  isLoading,
  isVisible,
  activeOrganization,
  resourceCategory,
}: EntryOutputModalProps): JSX.Element => {
  const dispatch = useDispatch();

  const resourceSearchEntities = useSelector(resourcesEntitiesSelector);
  const resourceSearchIds = useSelector(resourcesIdsSelector);
  const isLoadingResources = useSelector(isLoadingResourcesSelector);
  const resourceSearchResult = resourceSearchIds
    .map((resourceId) => resourceSearchEntities[resourceId])
    .filter((resource) => !!resource) as Resource[];
  const [searchIndex, setSearchIndex] = useState<number>();

  const [internalLoading, setInternalLoading] = useState(false);

  const [
    resourceSearchTimeout,
    setResourceSearchTimeout,
  ] = useState<number>();
  const onResourceSearch = (search: string, index: number) => {
    if (!search) {
      dispatch(cleanResourcesAction());
      setSearchIndex(undefined);
    } else {
      setInternalLoading(true);
      setSearchIndex(index);
      window.clearTimeout(resourceSearchTimeout);
      const searchTimeout = window.setTimeout(() => {
        dispatch(getResourceListAction({
          search,
          isPaginated: false,
          category: resourceCategory,
        }));
        setInternalLoading(false);
      }, 500);
      setResourceSearchTimeout(searchTimeout);
    }
  };

  useEffect(() => {
    const clearEffects = () => {
      window.clearTimeout(resourceSearchTimeout);
    };
    return clearEffects;
  }, []);

  const [selectedResources, setSelectedResources] = useState<(Resource | undefined)[]>([]);

  const internalOnCancel = () => {
    setSelectedResources([]);
    dispatch(cleanResourcesAction());
    onCancel();
  };

  const onEntryItemAdded = (
    formAdd: (defaultValue?: any, index?: number) => void, // eslint-disable-line
    index: number,
  ) => {
    const updatedSelectedResources = [
      ...selectedResources.slice(0, index),
      undefined,
      ...selectedResources.slice(index + 1),
    ];
    setSelectedResources(updatedSelectedResources);
    formAdd({
      resourceId: undefined,
    }, index);
  };

  const onEntryItemRemoved = (formRemove: (index: number | number[]) => void, index: number) => {
    const updatedSelectedResources = [
      ...selectedResources.slice(0, index),
      ...selectedResources.slice(index + 1),
    ];
    setSelectedResources(updatedSelectedResources);
    formRemove(index);
  };

  const onResourceSelect = (resource: Resource | undefined, index: number) => {
    if (resource) {
      const updatedSelectedResources = [
        ...selectedResources.slice(0, index),
        { ...resource },
        ...selectedResources.slice(index + 1),
      ];
      setSelectedResources(updatedSelectedResources);
      dispatch(cleanResourcesAction());
      setSearchIndex(undefined);
    }
  };

  return (
    <Modal
      visible={isVisible}
      title="Nova saída"
      destroyOnClose
      onCancel={internalOnCancel}
      afterClose={internalOnCancel}
      zIndex={90}
      width={720}
      footer={[
        <InlineButton
          key="cancel"
          onClick={internalOnCancel}
          type="ghost"
        >
          Cancelar
        </InlineButton>,
        <InlineButton
          key="submit"
          onClick={onSubmit}
          disabled={isLoading}
          loading={isLoading}
        >
          Salvar
        </InlineButton>,
      ]}
    >
      <FormContainer
        layout="vertical"
        form={formHandler}
        requiredMark={false}
        onFinish={onSubmit as (val: unknown) => void}
      >
        <BaseFormItem
          label="Unidade"
          name="organization"
          initialValue={activeOrganization.name}
        >
          <Input disabled />
        </BaseFormItem>
        <BaseFormItem
          label="Data de saída"
          name="entryDate"
          initialValue={dayjs()}
          rules={[
            {
              required: true,
              message: 'Insira uma data de saída',
            },
          ]}
        >
          <DatePicker disabled={isLoading} />
        </BaseFormItem>
        <FormItemRow>
          <BaseFormItem
            label="Motivo"
            name="outputType"
            rules={[
              {
                required: true,
                message: 'Selecione um motivo',
              },
            ]}
            initialValue={InventoryOutputTypeEnum.PRODUCTION}
          >
            <Select
              disabled={isLoading}
            >
              {Object.values(InventoryOutputTypeEnum).map((reason) => (
                <Select.Option value={reason} key={reason}>
                  {InventoryOutputReasonMapping[reason].name}
                </Select.Option>
              ))}
            </Select>
          </BaseFormItem>
          <BaseFormItem
            label="Centro de custo"
            name="costCenter"
            rules={[
              {
                required: true,
                message: 'Selecione um centro de custo',
              },
            ]}
            initialValue={CostCenterEnum.SEEDING}
          >
            <Select
              disabled={isLoading}
            >
              {Object.values(CostCenterEnum).map((costCenter) => (
                <Select.Option value={costCenter} key={costCenter}>
                  {InventoryCostCenterMapping[costCenter].name}
                </Select.Option>
              ))}
            </Select>
          </BaseFormItem>
        </FormItemRow>
        <h3>Itens</h3>
        <Form.List
          name="items"
          rules={[{
            message: 'Insira pelo menos um item',
            validator: () => {
              const formItems: EntryItemFormData[] = formHandler.getFieldValue('items');
              if (formItems.length > 0) {
                return Promise.resolve();
              }

              return Promise.reject();
            },
          }]}
        >
          {(fields, { add, remove }) => (
            <>
              {fields.map((itemField, index) => (
                <EntryItemFormRow
                  key={itemField.key}
                >
                  <EntryItemInput
                    label="Item"
                    style={{
                      flex: '2',
                    }}
                    name={[itemField.name, 'resource']}
                    validateTrigger="onSubmit"
                    rules={[
                      {
                        required: true,
                        message: 'Selecione um item',
                      },
                    ]}
                  >
                    <Select
                      placeholder="Selecione um item"
                      disabled={isLoading}
                      showArrow={false}
                      showSearch
                      onSearch={(searchValue) => onResourceSearch(searchValue, index)}
                      filterOption={false}
                      onSelect={(
                        resourceId,
                      ) => onResourceSelect(resourceSearchEntities[resourceId], index)}
                      notFoundContent={<></>}
                      dropdownRender={(menu) => (
                        <div>
                          <RemoteSelectContent>
                            {menu}
                            {(internalLoading || isLoadingResources) && (
                              <RemoteSelectSpinnerContainer>
                                <Spinner />
                              </RemoteSelectSpinnerContainer>
                            )}
                          </RemoteSelectContent>
                        </div>
                      )}
                    >
                      {searchIndex === index
                        ? resourceSearchResult.map((resourceOption) => (
                          <Select.Option
                            value={resourceOption.id}
                            key={resourceOption.id}
                          >
                            {resourceOption.name}
                          </Select.Option>
                        ))
                        : (() => {
                          const resourceOption = selectedResources[index];
                          return resourceOption ? (
                            <Select.Option
                              value={resourceOption.id}
                              key={resourceOption.id}
                            >
                              {resourceOption.name}
                            </Select.Option>
                          ) : null;
                        })()}
                    </Select>
                  </EntryItemInput>
                  <QuantityFormItem
                    label="Quantidade"
                    name={[itemField.name, 'quantity']}
                    rules={[
                      {
                        required: true,
                        message: 'Insira uma quantidade',
                      },
                    ]}
                  >
                    <Input
                      type="number"
                      disabled={isLoading || !selectedResources[index]}
                      suffix={(() => {
                        const item = selectedResources[index];
                        return item !== undefined ? UnitMapping[item.unit].name : '';
                      })()}
                    />
                  </QuantityFormItem>
                  <RemoveEntryItemButton onClick={() => onEntryItemRemoved(remove, index)}>
                    <RemoveEntryItemIcon />
                  </RemoveEntryItemButton>
                </EntryItemFormRow>
              ))}
              <AddEntryItemButton
                type="dashed"
                onClick={() => onEntryItemAdded(add, fields.length)}
                icon={<PlusOutlined size={14} />}
              >
                Adicionar item
              </AddEntryItemButton>
            </>
          )}
        </Form.List>
        <NotesInline
          label="Observações"
          name="notes"
        >
          <Input.TextArea disabled={isLoading} rows={5} />
        </NotesInline>
      </FormContainer>
    </Modal>
  );
};

export default EntryOutputModal;
